import React from 'react';
import { DealerReview } from '../../typings/dealer-review';
import { StyledDealerReviewCardContainer } from './styles';
import {
  Breakpoints,
  CustomImage,
  Internet,
  Link,
  Phone,
  TextCustom,
} from '@vw-marketing/us-components';
import { useFeatureAppConfig } from '../../services/use-feature-app-config';
import { AppConstants } from '../../utils/app-constants';
import {
  addressTextStyle,
  contactTextStyle,
  dealerNameTextStyle,
  overallTextStyle,
  reviewsTextStyle,
} from './helpers';
import FiveStars from '../five-stars';
import { useIsMobile } from '../use-is-mobile';
import { stringToPhoneFormat } from '../../utils/general';
import DisclaimerSection from '../disclaimer-section';
import _ from 'lodash';

interface DealerReviewCardProps {
  dealerReview?: DealerReview;
}
const DealerReviewCard: React.FC<DealerReviewCardProps> = ({
  dealerReview,
}) => {
  const { dealerConfiguration, fiveStarsConfig } = useFeatureAppConfig();
  const isMobile = useIsMobile(Breakpoints.b560);
  const hasReviews = !_.isEmpty(dealerReview);
  const overallRatingLabel = 'Overall Rating';
  const reviewsDisclaimer =
    fiveStarsConfig?.viewAllDisclaimer || AppConstants.allReviewsDisclaimer;
  return (
    <StyledDealerReviewCardContainer hasReviews={hasReviews}>
      <div className="dealer-name-wrapper notranslate">
        <TextCustom {...dealerNameTextStyle}>
          {dealerConfiguration?.name}
        </TextCustom>
      </div>
      <div className="dealer-map-wrapper">
        <CustomImage
          src={dealerConfiguration?.staticMapsUrl || ''}
          alt={dealerConfiguration?.name || ''}
          srcPlaceholder={AppConstants.PlaceHolder480x216 as string}
        />
      </div>
      {hasReviews && (
        <div className="rating-wrapper">
          <>
            <TextCustom {...overallTextStyle}>{overallRatingLabel}</TextCustom>
            <div className="rating-stars-wrapper">
              <FiveStars
                percent={((dealerReview?.avg_rating || 0) / 5) * 100}
                starSize={'18px'}
                starColor={AppConstants.StarColor as string}
                background={AppConstants.StarBackground as string}
              />
              <TextCustom {...reviewsTextStyle}>
                {dealerReview?.avg_rating?.toFixed(1)}
                {' | '}
                {`${dealerReview?.total_ratings} reviews`}
              </TextCustom>
            </div>{' '}
          </>
        </div>
      )}
      <div className="dealer-address-wrapper notranslate">
        <TextCustom {...addressTextStyle}>
          {dealerConfiguration?.address1}
        </TextCustom>
        <TextCustom {...addressTextStyle}>
          {dealerConfiguration?.city}, {dealerConfiguration?.state}{' '}
          {dealerConfiguration?.postalcode}
        </TextCustom>
      </div>

      <div className="dealer-contact-wrapper">
        <div className="dealer-contact-item notranslate">
          <Phone />
          {isMobile ? (
            <Link href={'tel:' + dealerConfiguration?.phone} isInline>
              <TextCustom {...contactTextStyle}>
                {stringToPhoneFormat(dealerConfiguration?.phone || '')}
              </TextCustom>{' '}
            </Link>
          ) : (
            <TextCustom {...contactTextStyle}>
              {stringToPhoneFormat(dealerConfiguration?.phone || '')}
            </TextCustom>
          )}
        </div>

        <div className="dealer-contact-item">
          <Internet />
          <Link href={dealerConfiguration?.url} target="_blank" isInline>
            <TextCustom {...contactTextStyle}>Website</TextCustom>
          </Link>
        </div>
      </div>

      <div className="dealer-reviews-disclaimer">
        <DisclaimerSection
          disclaimer={reviewsDisclaimer}
          hasReviews={hasReviews}
        />
      </div>
    </StyledDealerReviewCardContainer>
  );
};

export default DealerReviewCard;
